import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { GamificationConfigService } from "../core/gamification/config.service";
import { PracticeStore } from "../core/gamification/practice.store";
import {
  CatalogService,
  type CatalogChoice,
} from "../core/catalog/catalog.service";
import type {
  DifficultyTierId,
  Locale,
  ManeuverDef,
  ManeuverPhase,
  ManeuverStep,
} from "../core/gamification/gamification.types";

/** Payload emitted when the user starts a practice run. */
export interface PracticeLaunch {
  readonly maneuverId: string;
  readonly tier: DifficultyTierId;
  readonly boatId: string;
  readonly terrainId: string;
}

const TIER_IDS: readonly DifficultyTierId[] = [
  "calm",
  "moderate",
  "fresh",
  "gale",
];

/**
 * Read-only Practice setup screen. Renders four selects (maneuver / boat /
 * terrain / tier), a collapsible "how to do it" panel showing the chosen
 * maneuver's `commandSequence`, and Start / Cancel actions.
 *
 * Selections are persisted to {@link PracticeStore.lastSelections} on every
 * change so reopening the screen prefills the previous choice.
 *
 * The screen does NOT launch the sim itself — it emits a {@link PracticeLaunch}
 * payload via the `launch` output and lets the parent (AppComponent) decide
 * how to wire the sim. This keeps the component free of `SimService` coupling.
 */
@Component({
  selector: "po-practice-setup",
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./practice-setup.component.html",
  styleUrl: "./practice-setup.component.css",
})
export class PracticeSetupComponent {
  private readonly config = inject(GamificationConfigService);
  private readonly store = inject(PracticeStore);
  private readonly translate = inject(TranslateService);
  private readonly catalog = inject(CatalogService);

  readonly launch = output<PracticeLaunch>();
  readonly cancel = output<void>();

  protected readonly tiers = TIER_IDS;
  /**
   * Only maneuvers with a non-empty `commandSequence` are MVP-playable.
   * The rest are config placeholders whose requirements would either
   * trivially pass (empty `requirements: {}`) or rely on detection we
   * haven't wired yet — picking one would auto-complete in one tick.
   */
  protected readonly maneuvers = computed(() =>
    (this.config.maneuvers()?.maneuvers ?? []).filter(
      (m) => m.commandSequence.length > 0,
    ),
  );
  protected readonly configError = this.config.error;
  protected readonly configLoading = computed(() => !this.config.isLoaded());

  protected readonly terrains = signal<CatalogChoice[]>([]);
  protected readonly boats = signal<CatalogChoice[]>([]);
  protected readonly catalogLoading = signal(true);
  protected readonly catalogError = signal<string | null>(null);

  protected readonly selectedManeuverId = signal<string | null>(null);
  protected readonly selectedTier = signal<DifficultyTierId>("calm");
  protected readonly selectedBoatId = signal<string | null>(null);
  protected readonly selectedTerrainId = signal<string | null>(null);
  protected readonly howToOpen = signal(false);

  protected readonly currentLocale = signal<Locale>(this.detectLocale());

  protected readonly selectedManeuver = computed<ManeuverDef | null>(() => {
    const id = this.selectedManeuverId();
    if (!id) return null;
    return this.maneuvers().find((m) => m.id === id) ?? null;
  });

  protected readonly hasCommandSequence = computed(() => {
    const m = this.selectedManeuver();
    return !!m && m.commandSequence.length > 0;
  });

  protected readonly canStart = computed(
    () =>
      !this.configLoading() &&
      !this.catalogLoading() &&
      this.selectedManeuverId() !== null &&
      this.selectedBoatId() !== null &&
      this.selectedTerrainId() !== null,
  );

  constructor() {
    void this.config.load().catch(() => {
      /* error surfaced via config.error */
    });
    void this.loadCatalogs();
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((e) => {
      this.currentLocale.set(this.coerceLocale(e.lang));
    });

    // Once both catalogs + config are in, prefill from store.lastSelections.
    effect(() => {
      const maneuvers = this.maneuvers();
      const boats = this.boats();
      const terrains = this.terrains();
      if (
        maneuvers.length === 0 ||
        boats.length === 0 ||
        terrains.length === 0
      ) {
        return;
      }
      const last = this.store.lastSelections();
      if (this.selectedManeuverId() === null) {
        const mid =
          last.maneuverId && maneuvers.some((m) => m.id === last.maneuverId)
            ? last.maneuverId
            : maneuvers[0]!.id;
        this.selectedManeuverId.set(mid);
      }
      if (this.selectedBoatId() === null) {
        const bid =
          last.boatId && boats.some((b) => b.id === last.boatId)
            ? last.boatId
            : boats[0]!.id;
        this.selectedBoatId.set(bid);
      }
      if (this.selectedTerrainId() === null) {
        const tid =
          last.terrainId && terrains.some((t) => t.id === last.terrainId)
            ? last.terrainId
            : terrains[0]!.id;
        this.selectedTerrainId.set(tid);
      }
      if (last.tier) this.selectedTier.set(last.tier);
    });
  }

  protected updateManeuver(event: Event): void {
    const id = (event.target as HTMLSelectElement).value;
    this.selectedManeuverId.set(id);
    this.store.setLastSelections({ maneuverId: id });
  }

  protected updateTier(event: Event): void {
    const tier = (event.target as HTMLSelectElement).value as DifficultyTierId;
    if (!TIER_IDS.includes(tier)) return;
    this.selectedTier.set(tier);
    this.store.setLastSelections({ tier });
  }

  protected updateBoat(event: Event): void {
    const id = (event.target as HTMLSelectElement).value;
    this.selectedBoatId.set(id);
    this.store.setLastSelections({ boatId: id });
  }

  protected updateTerrain(event: Event): void {
    const id = (event.target as HTMLSelectElement).value;
    this.selectedTerrainId.set(id);
    this.store.setLastSelections({ terrainId: id });
  }

  protected toggleHowTo(): void {
    this.howToOpen.update((v) => !v);
  }

  protected start(): void {
    if (!this.canStart()) return;
    const maneuverId = this.selectedManeuverId();
    const boatId = this.selectedBoatId();
    const terrainId = this.selectedTerrainId();
    if (!maneuverId || !boatId || !terrainId) return;
    this.launch.emit({
      maneuverId,
      tier: this.selectedTier(),
      boatId,
      terrainId,
    });
  }

  protected close(): void {
    this.cancel.emit();
  }

  /** Localized label helper for maneuver/phase/step entries. */
  protected labelOf(labels: { en: string; pl: string }): string {
    return labels[this.currentLocale()];
  }

  protected trackPhase(_: number, phase: ManeuverPhase): string {
    return phase.id;
  }

  protected trackStep(_: number, step: ManeuverStep): string {
    return step.id;
  }

  private async loadCatalogs(): Promise<void> {
    this.catalogLoading.set(true);
    this.catalogError.set(null);
    try {
      const [terrains, boats] = await this.catalog.loadAll();
      if (terrains.length > 0) this.terrains.set([...terrains]);
      if (boats.length > 0) this.boats.set([...boats]);
    } catch (err) {
      this.catalogError.set(err instanceof Error ? err.message : String(err));
    } finally {
      this.catalogLoading.set(false);
    }
  }

  private detectLocale(): Locale {
    return this.coerceLocale(
      this.translate.currentLang ?? this.translate.getDefaultLang(),
    );
  }

  private coerceLocale(lang: string | undefined): Locale {
    return lang === "en" ? "en" : "pl";
  }
}
